import { useCallback, useEffect, useRef, useState } from 'react';
import { askStream as askStreamApi, type AskMode } from '@/lib/api-client';
import { useSession } from '@/contexts/SessionContext';
import type { ResponseCardState } from '@/components/ResponseCard';

interface FeedEntry {
  /** Stable client-side id used to update the entry in place. */
  attemptId: string;
  state: ResponseCardState;
}

export interface UseHearingSessionResult {
  /** Newest first. Bind directly to ResponseCard.map. */
  feed: FeedEntry[];
  /** True while an ask is in flight. Disables the ask buttons only -- never the
   *  transcription, which runs on independent state (see Section C). */
  busy: boolean;
  /** True if a session is ready and ask can be called. */
  ready: boolean;
  /** attemptId of the card currently being streamed, or null. */
  streamingAttemptId: string | null;
  /** Send a prompt in the given mode (default deep). Streams token by token. */
  ask: (prompt: string, label?: string | null, mode?: AskMode) => Promise<void>;
  /** Remove one response entry from the visible feed (after a server soft delete). */
  removeFromFeed: (responseId: string) => void;
  /** Remove all settled response entries from the feed; keeps any in-flight card. */
  clearFeed: () => void;
}

// Rough token estimate for the live count shown on the streaming card. chars/4
// is the standard heuristic — close enough for a progress indicator.
function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/**
 * Owns the ask flow for one hearing. The active session id comes from
 * SessionContext (eager-created on matter select). Each ask drops a streaming
 * placeholder card at the top of the feed, appends tokens as they arrive, then
 * swaps it for the saved response (or an error) when the stream completes.
 *
 * P2 Section 1: `ask` takes a mode ('fast' | 'deep'). The streaming card carries
 * the mode and a live token estimate; error cards carry an onRetry that re-fires
 * the same prompt in the same mode.
 */
export function useHearingSession(): UseHearingSessionResult {
  const { status, sessionId, initialResponses, reset: resetSession } = useSession();
  const [feed, setFeed] = useState<FeedEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [streamingAttemptId, setStreamingAttemptId] = useState<string | null>(null);

  // Seed (and reset) the feed from persisted responses whenever the session
  // changes, so a reload or matter switch shows the full session history,
  // newest first. New asks prepend to this.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionId) {
      setFeed([]);
      seededFor.current = null;
      return;
    }
    if (seededFor.current === sessionId) {
      return;
    }
    seededFor.current = sessionId;
    setFeed(
      [...initialResponses].reverse().map((r) => ({
        attemptId: r.id,
        state: { kind: 'response' as const, response: r },
      })),
    );
  }, [sessionId, initialResponses]);

  const ask = useCallback(
    async (prompt: string, label?: string | null, mode: AskMode = 'deep') => {
      const labelOrUndefined = label === null ? undefined : label;
      const attemptId = makeAttemptId();

      if (status !== 'ready' || !sessionId) {
        setFeed((prev) => [
          {
            attemptId,
            state: {
              kind: 'error',
              ...(labelOrUndefined !== undefined && { label: labelOrUndefined }),
              message:
                status === 'error'
                  ? 'Could not start the hearing session. Try selecting the matter again.'
                  : 'Select a matter before asking Claude.',
            },
          },
          ...prev,
        ]);
        return;
      }

      // Streaming placeholder at the top of the feed.
      setFeed((prev) => [
        {
          attemptId,
          state: {
            kind: 'streaming',
            ...(labelOrUndefined !== undefined && { label: labelOrUndefined }),
            text: '',
            mode,
            tokens: 0,
          },
        },
        ...prev,
      ]);
      setBusy(true);
      setStreamingAttemptId(attemptId);

      const setEntry = (state: ResponseCardState) =>
        setFeed((prev) =>
          prev.map((entry) => (entry.attemptId === attemptId ? { attemptId, state } : entry)),
        );

      // Closure that re-fires this exact prompt in the same mode (retry button).
      const retry = () => void ask(prompt, label, mode);

      try {
        const askInput: { sessionId: string; prompt: string; label?: string; mode: AskMode } = {
          sessionId,
          prompt,
          mode,
        };
        if (labelOrUndefined !== undefined) {
          askInput.label = labelOrUndefined;
        }

        await askStreamApi(askInput, {
          onDelta: (text) => {
            setFeed((prev) =>
              prev.map((entry) => {
                if (entry.attemptId !== attemptId || entry.state.kind !== 'streaming') {
                  return entry;
                }
                const nextText = entry.state.text + text;
                return {
                  attemptId,
                  state: { ...entry.state, text: nextText, tokens: estimateTokens(nextText) },
                };
              }),
            );
          },
          onDone: (envelope) => {
            setEntry({ kind: 'response', response: envelope.response });
          },
          onError: ({ code, message }) => {
            if (code === 'SESSION_ENDED') {
              // Cached session id was stale (server-side end). Force a fresh one.
              void resetSession();
            }
            setEntry({
              kind: 'error',
              ...(labelOrUndefined !== undefined && { label: labelOrUndefined }),
              message: friendlyError(code, message),
              onRetry: retry,
            });
          },
        });
      } catch (err) {
        setEntry({
          kind: 'error',
          ...(labelOrUndefined !== undefined && { label: labelOrUndefined }),
          message: err instanceof Error ? err.message : 'Something went wrong.',
          onRetry: retry,
        });
      } finally {
        setBusy(false);
        setStreamingAttemptId(null);
      }
    },
    [status, sessionId, resetSession],
  );

  // Remove a settled response from the feed (the server soft-delete is done by
  // the caller). Matches by the response id, which is the attemptId for seeded
  // rows and is also carried on the saved entry.
  const removeFromFeed = useCallback((responseId: string) => {
    setFeed((prev) =>
      prev.filter((entry) =>
        entry.state.kind === 'response' ? entry.state.response.id !== responseId : true,
      ),
    );
  }, []);

  // Drop all settled response entries (keep a streaming/loading/error card so an
  // in-flight ask is never yanked out from under the operator).
  const clearFeed = useCallback(() => {
    setFeed((prev) => prev.filter((entry) => entry.state.kind !== 'response'));
  }, []);

  return {
    feed,
    busy,
    ready: status === 'ready' && sessionId !== null,
    streamingAttemptId,
    ask,
    removeFromFeed,
    clearFeed,
  };
}

function makeAttemptId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `att_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function friendlyError(code: string, message: string): string {
  // P4.2: distinguish genuine connectivity loss from a gateway/SSE timeout from a
  // mid-stream interruption, so the operator knows whether to check their internet
  // or just retry (or switch to fast mode). Every one of these keeps the retry
  // button (useHearingSession.onError always attaches onRetry).
  if (code === 'NO_CONNECTION' || code === 'NETWORK') {
    return message || 'No connection to the server. Check your internet and retry.';
  }
  if (code === 'STREAM_INTERRUPTED') {
    return message || 'The response was interrupted before it finished. Tap retry.';
  }
  if (code === 'TIMEOUT') {
    return (
      message ||
      'The response took longer than 5 minutes. Tap retry, or use fast mode for an instant answer.'
    );
  }
  if (code === 'RATE_LIMITED') {
    return message;
  }
  if (code === 'AUTH_REQUIRED') {
    return 'Session expired. Please sign in again.';
  }
  if (code === 'UPSTREAM') {
    return `Anthropic call failed: ${message}`;
  }
  return message;
}
